import { useMemo, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import { useListGoals } from '@/api/generated';
import {
  useListStrategicPlans,
  usePatchStrategicPlan,
  ListStrategicPlansTimeFrame,
  useCreateStrategicPlan,
  getListStrategicPlansQueryKey
} from '@/api/generated';

const useStrategicPlan = (year?: number, quarter?: number) => {
  const { captureEvent } = useAnalytics();
  const { data: strategicPlansResponse, isLoading: isLoadingStrategicPlans } =
    useListStrategicPlans(
      {
        year: year?.toString(),
        timeFrame: `Q${quarter}` as ListStrategicPlansTimeFrame
      },
      {
        query: {
          queryKey: getListStrategicPlansQueryKey({
            year: year?.toString(),
            timeFrame: `Q${quarter}` as ListStrategicPlansTimeFrame
          })
        }
      }
    );
  const { mutate: updateStrategicPlan } = usePatchStrategicPlan();

  const { refetch: refetchGoals } = useListGoals(undefined, {
    query: {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: Infinity
    }
  });

  const {
    mutate: createStrategicPlanMutation,
    mutateAsync: createStrategicPlanMutationAsync,
    isPending: isCreating
  } = useCreateStrategicPlan();

  const queryClient = useQueryClient();

  const createStrategicPlan = useCallback(
    (year: number, quarter: number, onSuccessCallback?: () => void) => {
      createStrategicPlanMutation(
        {
          data: {
            year: year.toString(),
            timeFrame: `Q${quarter}` as ListStrategicPlansTimeFrame
          }
        },
        {
          onSuccess: data => {
            // Update the strategic plans cache with the new plan
            queryClient.setQueryData(
              getListStrategicPlansQueryKey({
                year: year.toString(),
                timeFrame: `Q${quarter}` as ListStrategicPlansTimeFrame
              }),
              (old: any) => {
                if (!old?.items) {
                  return {
                    items: data?.items || []
                  };
                }
                return {
                  ...old,
                  items: [...old.items, ...(data?.items || [])]
                };
              }
            );
            onSuccessCallback?.();
          }
        }
      );
    },
    [createStrategicPlanMutation, queryClient]
  );

  const strategicPlan = useMemo(() => {
    return strategicPlansResponse?.items?.[0];
  }, [strategicPlansResponse]);

  const onEditStrategicPlan = useCallback(
    (value: string, id: string) => {
      updateStrategicPlan(
        {
          id: id,
          data: {
            plan: value
          }
        },
        {
          onSuccess: () => {
            captureEvent(EVENTS.PLAN_EDITED, {
              id,
              plan: value
            });
            queryClient.setQueryData(
              getListStrategicPlansQueryKey({
                year: year?.toString(),
                timeFrame: `Q${quarter}` as ListStrategicPlansTimeFrame
              }),
              (old: any) => {
                if (!old?.items) return old;
                return {
                  ...old,
                  items: old.items.map((plan: any) =>
                    plan.id === id ? { ...plan, plan: value } : plan
                  )
                };
              }
            );
          }
        }
      );
    },
    [updateStrategicPlan, queryClient, year, quarter]
  );

  return {
    strategicPlan,
    onEditStrategicPlan,
    createStrategicPlan,
    createStrategicPlanMutationAsync,
    isCreating,
    isLoadingStrategicPlans,
    refetchGoals
  };
};

export default useStrategicPlan;
